'use client'

import { useSyncExternalStore } from 'react'
import { Chip, StatusPageContent } from '@sim/emcn'
import { NextIntlClientProvider, useTranslations } from 'next-intl'
/** omnyqra: Omnyqra brand fonts instead of Season Sans (Omnyqra/sim#14), see FORK.md */
import { omnyqraFontVariables } from '@/app/_styles/fonts/omnyqra/omnyqra'
import { LogoShell } from '@/app/(landing)/components/logo-shell'
import { DEFAULT_LOCALE, type Locale, resolveLocale } from '@/i18n/config'
import { getLocaleCookie, subscribeLocaleCookie } from '@/i18n/set-locale'
import deErrors from '@/messages/de/errors.json'
import enErrors from '@/messages/en/errors.json'
import '@/app/_styles/globals.css'

interface GlobalErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

/**
 * omnyqra: UI language (Omnyqra/sim#49), see FORK.md.
 *
 * `global-error` replaces the root layout, so neither the server-resolved locale
 * nor its `NextIntlClientProvider` exist here. The page resolves the language
 * in the browser by the same rules (cookie, then browser languages) and ships
 * its own messages. The server snapshot is English, the hydrated page switches.
 */
const ERROR_MESSAGES: Record<Locale, { errors: typeof enErrors }> = {
  en: { errors: enErrors },
  de: { errors: deErrors },
}

function getBrowserLocale(): Locale {
  const languages = navigator.languages ?? [navigator.language]
  return resolveLocale(getLocaleCookie() ?? undefined, languages.join(','))
}

function getServerLocale(): Locale {
  return DEFAULT_LOCALE
}

function GlobalErrorContent({ reset }: Pick<GlobalErrorProps, 'reset'>) {
  const t = useTranslations('errors')
  return (
    <LogoShell center>
      <StatusPageContent title={t('global.title')} description={t('global.description')}>
        <Chip variant='primary' onClick={reset}>
          {t('common.tryAgain')}
        </Chip>
      </StatusPageContent>
    </LogoShell>
  )
}

export default function GlobalError({ reset }: GlobalErrorProps) {
  const locale = useSyncExternalStore(subscribeLocaleCookie, getBrowserLocale, getServerLocale)

  return (
    <html lang={locale} className='light'>
      <body className={`${omnyqraFontVariables} font-season`}>
        <NextIntlClientProvider locale={locale} messages={ERROR_MESSAGES[locale]}>
          <GlobalErrorContent reset={reset} />
        </NextIntlClientProvider>
      </body>
    </html>
  )
}
